import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { sentenceCase, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsPhoneNumberInput from 'ui-elements/form-elements/FsPhoneNumberInput';
import FsPasswordInput from 'ui-elements/form-elements/FsPasswordInput';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import { isWeekPasswordRe } from 'ui-elements/form-elements/FsPasswordStrengthChecker/PasswordStrengthChecker.utils';

export interface FsRegisterFromInvitationDataForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  phoneNumber: string;
  formattedPhoneNumber?: string;
}

interface FsRegisterFromInvitationFormProps
  extends FsRegisterFromInvitationDataForm {
  onClose?: () => void;
  onSuccess?: (props: FsRegisterFromInvitationDataForm) => void;
}

const FsRegisterFromInvitationForm: React.FC<FsRegisterFromInvitationFormProps> = ({
  onClose,
  onSuccess,
  ...defaults
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [firstName, setFirstName] = useState<string>(defaults.firstName);
  const [lastName, setLastName] = useState<string>(defaults.lastName);
  const [email, setEmail] = useState<string>(defaults.email);
  const [password, setPassword] = useState<string>(defaults.password);
  const [repeatPassword, setRepeatPassword] = useState<string>(
    defaults.repeatPassword
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(
    defaults.phoneNumber || ''
  );
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState<string>(
    defaults.phoneNumber || ''
  );
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      firstName,
      lastName,
      password,
      repeatPassword,
      email,
      phoneNumber,
      formattedPhoneNumber,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    if (onSuccess)
      onSuccess({
        firstName,
        lastName,
        password,
        repeatPassword,
        email,
        phoneNumber,
        formattedPhoneNumber,
      });
  };

  const fields = [
    {
      required: true,
      name: defaultLocales.firstName.toLowerCase(),
      value: firstName,
      label: defaultLocales.firstName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFirstName(e.target?.value);
      },
      autoFocus: true,
    },
    {
      name: defaultLocales.lastName.toLowerCase(),
      value: lastName,
      label: defaultLocales.lastName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLastName(e.target?.value);
      },
    },
    {
      required: true,
      disabled: true,
      name: defaultLocales.email.toLowerCase(),
      value: email,
      label: defaultLocales.email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target?.value);
      },
    },
    {
      required: true,
      name: defaultLocales.password.toLowerCase(),
      type: 'password',
      value: password,
      label: defaultLocales.createPassword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setPassword(e.target?.value);
      },
      showStrengthChecker: true,
      showInfo: true,
    },
    {
      required: true,
      name: 'repeat-password',
      type: 'password',
      value: repeatPassword,
      label: defaultLocales.repeatPassword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRepeatPassword(e.target?.value);
      },
    },
    {
      required: true,
      name: 'phone',
      value: phoneNumber,
      label: defaultLocales.phoneNumber,
      country: 'gb',
      onChange: (
        value: string,
        country: never,
        e: never,
        formattedValue: string
      ): void => {
        setPhoneNumber(value);
        setFormattedPhoneNumber(formattedValue);
      },
      onKeyDown: async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid) {
          await handleSubmit();
        }
      },
    },
  ];

  const [firstNameField, lastNameField, ...otherFields] = fields;

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isPhoneValid = phoneNumber.length >= 5;
    const isNameValid = firstName.length >= 3;
    const isPasswordValid =
      !!password.match(isWeekPasswordRe) && password === repeatPassword;
    const isValid =
      isDefaultFormValid && isPhoneValid && isNameValid && isPasswordValid;
    setIsFormValid(isValid);
  }, [firstName, lastName, email, password, repeatPassword, phoneNumber]);

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        <FsFormGridWrapper>
          {[firstNameField, lastNameField].map(
            ({ name, onChange, ...otherProps }) => (
              <FsFormGridItem key={name}>
                <FsSimpleInput
                  {...{ ...otherProps, name }}
                  onChange={
                    onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                  }
                  fsVariant="outlined"
                  fullWidth
                />
              </FsFormGridItem>
            )
          )}
        </FsFormGridWrapper>
        {otherFields.map(({ name, label, onChange, ...otherProps }) => (
          <FsFormFullWidthContainer key={name}>
            {name === 'phone' && (
              <FsPhoneNumberInput
                {...otherProps}
                onChange={onChange as (phoneNumber: string) => void}
                placeholder={`${defaults.phoneNumber} *`}
              />
            )}
            {name.includes('password') && (
              <FsPasswordInput
                {...{ ...otherProps, label }}
                onChange={
                  onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                }
                placeholder={`${sentenceCase(name)} *`}
                fsVariant="outlined"
                fullWidth
              />
            )}
            {name !== 'phone' && !name.includes('password') && (
              <FsSimpleInput
                {...{ ...otherProps, name, label }}
                onChange={
                  onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                }
                fsVariant="outlined"
                fullWidth
              />
            )}
          </FsFormFullWidthContainer>
        ))}
        <FsPendingButton
          type="submit"
          variant="contained"
          color="primary"
          pending={pending}
          disabled={!isFormValid}
          endIcon={<FsNextArrowIcon />}
          fullWidth
          spaceBetween
        >
          {defaultLocales.continue}
        </FsPendingButton>
      </form>
    </Box>
  );
};

export default FsRegisterFromInvitationForm;
